import React, { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Button from 'reactstrap/lib/Button';
import InputField from '../../../components/Form/InputField';
import ErrorMessages from '../../../components/UI/ErrorMessages';
import delay from '../../../helpers/delay';
import translateError from '../../../helpers/translateError';
import { validPhone } from '../../../helpers/valid';
import { validatePhone } from '../../../helpers/zipps';
import messageWhatsappPost from '../../../services/messageWhatsappPost';
import ApiError from '../../../types/ApiError';
import { ServiceError } from '../../../types/ServiceError';
import styles from './PhoneValidation.module.scss';
import ValidationStatusField from './ValidationStatusField';

const delayAttemptTime = 30; // 30s

interface Props {
  phone: string;
  setIsValidated: (validated: boolean) => void;
  setFieldError: (error: string) => void;
  setPhone: (value: string) => void;
  sendDisabled?: boolean;
  authenticated?: boolean;
}

type Status = 'init' | 'sending' | 'sent' | 'error';

const PhoneValidationField = ({
  phone,
  setIsValidated,
  setFieldError,
  setPhone,
  sendDisabled = false,
  authenticated = false,
}: Props) => {
  const [sendStatus, setSendStatus] = useState<Status>('init');
  const [error, setError] = useState<string[]>([]);
  const [timeLeft, setTimeLeft] = useState<number>(delayAttemptTime);
  const [delayAttempt, setDelayAttempt] = useState<boolean>(false);

  const [isValid, setIsValid] = useState(false);
  const [lastValue, setLastValue] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const handleBlur = async () => {
    if (phone) {
      if (phone !== lastValue) {
        setLastValue(phone);
        if (validPhone(phone)) {
          setIsValidating(true);
          setIsValid(false);
          setIsValidated(false);

          const { valid, exceededAttemps } = await validatePhone(phone);
          setDisabled(exceededAttemps);
          setFieldError(valid ? "" : "El número que ingresaste no existe o no es válido");

          setIsValidating(false);
          setIsValid(valid);
          setIsValidated(valid);
        } else {
          setIsValid(false);
          setFieldError("El número de móvil no es válido");
          setIsValidated(false);
        }
      }
    } else {
      setIsValid(false);
      setIsValidated(false);
    }
  };

  const sendCodeValidation = async () => {
    if (!phone || phone.length < 10) return;
    try {
      setSendStatus('sending');
      await messageWhatsappPost({ phone: phone }, authenticated);
      setSendStatus('sent');
      setError([]);
      setDelayAttempt(true);
      setTimeLeft(delayAttemptTime);
      const interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      await delay(delayAttemptTime * 1000);
      setDelayAttempt(false);
    } catch (error) {

      if (
        error instanceof ApiError &&
        error.response &&
        error.response.errors
      ) {
        const serviceErrors = error.response.errors as ServiceError[];
        const errors = serviceErrors.map((errorItem: ServiceError) =>
          translateError(errorItem.error)
        );
        setError(errors.length ? errors : ['Ocurrió un error inesperado, inténtalo más tarde']);
      } else {
        setError(['Ocurrió un error inesperado, inténtalo más tarde']);
      }
      setSendStatus('error');
    }
  };

  return (
    <div className={styles.phoneValidation}>
      <div onBlur={handleBlur} className={styles.main}>
        <label className={styles.label}>Móvil</label>
        <div className={styles.content}>
          <PhoneInput
            country={'co'}
            value={phone}
            onBlur={handleBlur}
            onChange={(value: string) => {
              setPhone(`+${value}`);
              setIsValid(false);
              setIsValidated(false);
              setFieldError("");
            }}
            inputProps={{
              name: 'phone',
              required: true,
              disabled: disabled,
            }}
            enableSearch
            inputStyle={{
              width: '100%',
              height: '40px',
              fontSize: '1.1rem',
            }}
          />
          <ValidationStatusField isValid={isValid} isValidating={isValidating} />
        </div>
        {sendDisabled && isValid && (
          <Button
            id="form-otp"
            color="primary"
            className={styles.button}
            disabled={sendStatus === 'sending' || !sendDisabled || delayAttempt || !isValid || isValidating}
            onClick={sendCodeValidation}
          >
            {sendStatus === 'sending'
              ? 'Enviando'
              : delayAttempt
                ? timeLeft
                : 'Enviar código'}
          </Button>
        )}
      </div>

      {!!error && <ErrorMessages errorMessages={error} />}
      <InputField
        name="code"
        label="Código"
        description="Ingresa el código que te hemos enviado a WhatsApp"
        disabled={isValidating || !isValid}
      />
      <div className={styles.spacing}></div>
    </div>
  );
};

export default PhoneValidationField;
